import React, { useRef, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import { CameraAlt, Cameraswitch } from '@mui/icons-material';

const MAX_SIZE_BYTES = 1024 * 1024; // 1MB

// Downscale image to under 1MB as JPG
const downscaleToMaxSize = async (blob, maxBytes = MAX_SIZE_BYTES) => {
    const img = document.createElement('img');
    const url = URL.createObjectURL(blob);

    return new Promise((resolve) => {
        img.onload = async () => {
            URL.revokeObjectURL(url);
            let width = img.width;
            let height = img.height;
            let quality = 0.9;

            while (true) {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                const result = await new Promise((res) => {
                    canvas.toBlob((b) => res(b), 'image/jpeg', quality);
                });

                if (!result || result.size <= maxBytes || width < 100 || height < 100) {
                    resolve(result || blob);
                    return;
                }
                if (quality > 0.5) {
                    quality -= 0.1;
                } else {
                    width = Math.floor(width * 0.8);
                    height = Math.floor(height * 0.8);
                    quality = 0.9;
                }
            }
        };
        img.src = url;
    });
};

const MemberPictureCamera = ({ open, onClose, onCapture }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const [capturing, setCapturing] = useState(false);
    const [error, setError] = useState(null);
    const [facingMode, setFacingMode] = useState('user');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

    const startCamera = async (mode) => {
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: mode, width: { ideal: 640 }, height: { ideal: 640 } }
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setError(null);
        } catch (err) {
            console.error('Camera error:', err);
            setError(err.name === 'NotAllowedError'
                ? 'Camera access denied. Please allow camera permission.'
                : 'Unable to access camera. Please check your device.');
        }
    };

    useEffect(() => {
        if (!open) return;

        setError(null);
        setFacingMode('user');

        const initCamera = async () => {
            try {
                await startCamera('user');
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoInputs = devices.filter((d) => d.kind === 'videoinput');
                setHasMultipleCameras(videoInputs.length >= 2);
            } catch (err) {
                setHasMultipleCameras(false);
            }
        };

        initCamera();
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
        };
    }, [open]);

    const handleSwitchCamera = async () => {
        const nextMode = facingMode === 'user' ? 'environment' : 'user';
        setFacingMode(nextMode);
        await startCamera(nextMode);
    };

    const handleCapture = async () => {
        if (!videoRef.current || capturing) return;

        setCapturing(true);
        try {
            const video = videoRef.current;
            const minDim = Math.min(video.videoWidth, video.videoHeight);
            // Zoom in: crop 65% of frame so head (which fills the circle in the popup) appears larger in the display
            const cropSize = Math.floor(minDim * 0.65);
            const size = minDim;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');

            // Center crop - smaller source region zooms in on the face
            const sx = (video.videoWidth - cropSize) / 2;
            const sy = (video.videoHeight - cropSize) / 2;
            ctx.drawImage(video, sx, sy, cropSize, cropSize, 0, 0, size, size);

            const blob = await new Promise((resolve) => {
                canvas.toBlob(resolve, 'image/jpeg', 0.9);
            });

            if (blob) {
                const downscaled = await downscaleToMaxSize(blob);
                const reader = new FileReader();
                reader.onloadend = () => {
                    onCapture(reader.result);
                    onClose();
                };
                reader.readAsDataURL(downscaled);
            }
        } catch (err) {
            console.error('Capture error:', err);
            setError('Failed to capture picture');
        } finally {
            setCapturing(false);
        }
    };

    const handleClose = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Take Picture</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    {error && (
                        <Box sx={{ color: 'error.main', textAlign: 'center', p: 1 }}>{error}</Box>
                    )}
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: 320,
                            aspectRatio: 1,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            bgcolor: 'black',
                        }}
                    >
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                minWidth: '100%',
                                minHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                            }}
                        />
                        {/* Circle overlay border to show framing */}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                border: '4px solid rgba(255,255,255,0.8)',
                                boxSizing: 'border-box',
                                pointerEvents: 'none',
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                            Position your head in the center
                        </Typography>
                        {hasMultipleCameras && (
                            <Tooltip title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}>
                                <IconButton
                                    onClick={handleSwitchCamera}
                                    disabled={!!error}
                                    color="primary"
                                    aria-label="Switch camera"
                                >
                                    <Cameraswitch />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleCapture}
                    disabled={capturing || !!error}
                    startIcon={<CameraAlt />}
                >
                    {capturing ? 'Capturing...' : 'Take Picture'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MemberPictureCamera;
